import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import PaymentModal from '../components/PaymentModal';
import {
  CheckCircleIcon,
  XCircleIcon,
  CreditCardIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

const Subscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, currentRes, historyRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/current'),
        api.get('/subscriptions/history'),
      ]);

      setPlans(plansRes.data.plans || []);
      setCurrentSubscription(currentRes.data.subscription);
      setSubscriptionHistory(historyRes.data.subscriptions);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      const response = await api.post('/subscriptions/create', {
        planId,
        autoRenew: true,
      });

      if (response.data.paymentRequired) {
        setSelectedPackage(response.data.subscription);
        setShowPaymentModal(true);
      } else {
        toast.success('Subscription created successfully!');
        fetchData();
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to create subscription');
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    try {
      await api.put(`/subscriptions/cancel/${subscriptionId}`);
      toast.success('Subscription cancelled successfully!');
      fetchData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatLimit = (value) => {
    if (value === null || value === -1) return 'Unlimited';
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
            <CreditCardIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="page-title">Subscriptions</h1>
            <p className="text-sm text-neutral-500">Manage your plans and view billing history</p>
          </div>
        </div>
      </div>

      {/* Current Subscription */}
      {currentSubscription && (
        <div className="card bg-gradient-to-br from-white via-white to-primary-50/30 border border-primary-100">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <SparklesIcon className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-neutral-800">Current Subscription</h3>
              </div>
              <p className="text-sm text-neutral-500">Your active plan details</p>
            </div>
            <span className={`badge ${
              currentSubscription.status === 'active' ? 'badge-green' : 'badge-yellow'
            }`}>
              {currentSubscription.status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Plan</dt>
              <dd className="mt-1 text-sm font-semibold text-neutral-800">{currentSubscription.planName}</dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Price</dt>
              <dd className="mt-1 text-sm font-semibold text-primary-600">
                {formatCurrency(currentSubscription.price, currentSubscription.currency || 'ZAR')}
              </dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Start Date</dt>
              <dd className="mt-1 text-sm font-semibold text-neutral-800">{formatDate(currentSubscription.startDate)}</dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">End Date</dt>
              <dd className="mt-1 text-sm font-semibold text-neutral-800">{formatDate(currentSubscription.endDate)}</dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Auto Renew</dt>
              <dd className="mt-1 flex items-center gap-1">
                {currentSubscription.autoRenew ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-600">Yes</span>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-500">No</span>
                  </>
                )}
              </dd>
            </div>
          </div>

          {currentSubscription.status === 'active' && (
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <button
                onClick={() => handleCancelSubscription(currentSubscription.id)}
                className="btn-danger btn-sm"
              >
                Cancel Subscription
              </button>
            </div>
          )}
        </div>
      )}

      {/* Available Plans */}
      <div className="card">
        <div className="mb-8 text-center">
          <h3 className="text-2xl font-bold text-neutral-800">Choose Your Plan</h3>
          <p className="mt-2 text-neutral-500">Select the perfect plan for your business needs</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan, index) => {
            const isPopular = plan.slug === 'professional';
            const features = [
              `${formatLimit(plan.messageLimit)} messages/mo`,
              `${formatLimit(plan.workspaceLimit)} workspace${plan.workspaceLimit !== 1 ? 's' : ''}`,
              `${formatLimit(plan.maxTeamMembers)} team member${plan.maxTeamMembers !== 1 ? 's' : ''}`,
              ...(plan.features || []),
            ];
            return (
              <div
                key={plan.id}
                className={`relative rounded-xl border-2 p-6 transition-all duration-300 ${
                  isPopular
                    ? 'border-primary-500 bg-primary-50/30 shadow-orange'
                    : 'border-neutral-200 bg-white hover:border-primary-300 hover:shadow-md'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h4 className="text-lg font-bold text-neutral-800">{plan.name}</h4>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-neutral-900">
                      {formatCurrency(plan.price, plan.currency).split('.')[0]}
                    </span>
                    <span className="text-neutral-500">/month</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        isPopular ? 'bg-primary-500' : 'bg-green-500'
                      }`}>
                        <CheckIcon className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-sm text-neutral-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={currentSubscription?.status === 'active'}
                  className={`w-full ${
                    isPopular ? 'btn-primary' : 'btn-outline'
                  } flex items-center justify-center gap-2`}
                >
                  {currentSubscription?.status === 'active' ? (
                    'Already Subscribed'
                  ) : (
                    <>
                      Get Started
                      <ArrowRightIcon className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Subscription History */}
      {subscriptionHistory.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6">Subscription History</h3>
          <div className="table-container">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Plan</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Price</th>
                  <th className="table-header-cell">Start Date</th>
                  <th className="table-header-cell">End Date</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {subscriptionHistory.map((subscription) => (
                  <tr key={subscription.id} className="table-row-hover">
                    <td className="table-cell font-medium text-neutral-800">
                      {subscription.planName}
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${
                        subscription.status === 'active' ? 'badge-green' :
                        subscription.status === 'cancelled' ? 'badge-red' : 'badge-yellow'
                      }`}>
                        {subscription.status}
                      </span>
                    </td>
                    <td className="table-cell font-medium text-primary-600">
                      {formatCurrency(subscription.price, subscription.currency || 'ZAR')}
                    </td>
                    <td className="table-cell">
                      {formatDate(subscription.startDate)}
                    </td>
                    <td className="table-cell">
                      {formatDate(subscription.endDate)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        subscription={selectedPackage}
        onPaymentSuccess={() => {
          setShowPaymentModal(false);
          fetchData();
        }}
      />
    </div>
  );
};

export default Subscriptions;
